// src/components/Layout/LanguageSelector.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { FaGlobe, FaChevronDown } from 'react-icons/fa';
import { SUPPORTED_LANGUAGES } from '../../utils/translationService';

const LanguageSelector = ({ color = '#ffffff', supportedLanguages: propSupportedLanguages }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentLanguage, changeLanguage } = useLanguage();
  const dropdownRef = useRef(null);

  // Build the language list: always an array of { code, name }
  const languageList = Array.isArray(propSupportedLanguages) ? propSupportedLanguages : [];

  const getDisplayName = (code) => {
    const found = Object.values(SUPPORTED_LANGUAGES).find(l => l.code === code);
    return found ? found.name : code;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageSelect = (language) => {
    changeLanguage(language);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-center w-[6rem] gap-1 px-2 py-1.5 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all duration-300"
        style={{ color }}
      >
        {/* <FaGlobe className="mr-1" size={14} /> */}
        <span className="text-sm font-medium mr-1">
          {currentLanguage.name || currentLanguage.code}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <FaChevronDown size={10} />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-1 w-full bg-white rounded-md z-50 text-sm text-gray-700"
            style={{
              maxHeight: '150px',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <style>
              {`
                /* Hide scrollbar for Chrome, Safari and Opera */
                .hide-scrollbar::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            <div className="hide-scrollbar">
              {(() => {
                const selected = languageList.find(l => l.code === currentLanguage.code);
                const rest = languageList.filter(l => l.code !== currentLanguage.code);
                const ordered = selected ? [selected, ...rest] : languageList;
                return ordered.map((language, idx) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageSelect(language)}
                    className={`flex items-center justify-center shadow-md border-b-2 border-[#2888b5] rounded-md w-full py-2 text-left hover:bg-gray-100
                      ${language.code === currentLanguage.code
                        ? 'bg-white font-bold'
                        : 'bg-[#d0e5ef]'
                      }
                    `}
                  >
                    {language.name}
                  </button>
                ));
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;